import React, { FC } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  SnackbarContent,
  type SnackbarCloseReason
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import type { FallbackProps } from "react-error-boundary";

export const ErrorPage: FC<FallbackProps> = ({
  error,
  resetErrorBoundary
}) => {
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const resolvedError =
    error instanceof Error ? error : new Error("An unexpected error occurred.");

  const handleCopy = () => {
    if (resolvedError.stack) {
      navigator.clipboard.writeText(resolvedError.stack);
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (
    event?: Event | React.SyntheticEvent,
    reason?: SnackbarCloseReason
  ) => {
    void event;
    void reason;
    if (!snackbarOpen) {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        p: 3,
        bgcolor: (theme) => theme.palette.MFT_DARK_BLUE
      }}
    >
      <Paper
        elevation={5}
        sx={{
          p: 4,
          maxWidth: 800,
          position: "relative",
          textAlign: "center",
          bgcolor: (theme) => theme.palette.MFT_OFF_WHITE
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ErrorOutlineIcon color="error" sx={{ fontSize: 50, mb: 2, mr: 2 }} />
          <Typography variant="h5" gutterBottom>
            Something went wrong
          </Typography>
        </Box>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          {resolvedError.message || "An unexpected error occurred."}
        </Typography>
        <Box>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            Try reloading the page. If the error persist copy the stack trace
            and send to IT/TECH
          </Typography>
        </Box>

        {resolvedError.stack && (
          <Box sx={{ mt: 3, textAlign: "left", position: "relative" }}>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                maxHeight: 300,
                overflow: "auto",
                bgcolor: "#e0e0e0",
                p: 2,
                borderRadius: 1,
                whiteSpace: "pre-wrap"
              }}
            >
              {resolvedError.stack}
            </Typography>
            <Tooltip title="Copy stack trace">
              <IconButton
                onClick={handleCopy}
                sx={{ position: "absolute", top: 8, right: 8 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Button
          variant="outlined"
          sx={{ mt: "16px" }}
          onClick={resetErrorBoundary}
        >
          Try reset error
        </Button>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={handleSnackbarClose}
        >
          <SnackbarContent
            message="Stack trace copied"
            sx={{
              bgcolor: (theme) => theme.palette.MFT_GREEN,
              color: (theme) => theme.palette.common.black
            }}
          />
        </Snackbar>
      </Paper>
    </Box>
  );
};
